import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useLocalStorage } from 'common/hooks';

import useBestOfWoltFeatureFlag from './useBestOfWoltFeatureFlag';
import { useBestOfWoltRoutes } from './useBestOfWoltRoutes';
import { useVenueInsightsPermissions } from './useVenueInsightsPermissions';

type UseBestOfWoltModalProps = {
  key: string;
  venue: Venue;
};

export const useBestOfWoltModal = ({ key, venue }: UseBestOfWoltModalProps) => {
  const hasVenueInsightsPermissions = useVenueInsightsPermissions(venue);
  const [wasModalShownBefore, setWasModalShownBefore] = useLocalStorage(key, 'false');
  const { isBestOfWoltEnabled, isLoading } = useBestOfWoltFeatureFlag({
    venueId: venue.id,
    venueCountry: venue.country,
    venueCity: venue.city,
    enabled: hasVenueInsightsPermissions,
  });

  const shouldShowModal =
    !isLoading && isBestOfWoltEnabled && wasModalShownBefore === 'false' && hasVenueInsightsPermissions;

  const history = useHistory();
  const { indexLink } = useBestOfWoltRoutes();

  const resetModal = useCallback(() => {
    if (wasModalShownBefore === 'true') {
      setWasModalShownBefore('false');
    }
  }, [wasModalShownBefore, setWasModalShownBefore]);

  const hideModal = () => {
    setWasModalShownBefore('true');
  };

  const navigateToBestOfWolt = () => {
    history.push(indexLink);
    hideModal();
  };

  return {
    shouldShowModal,
    resetModal,
    hideModal,
    navigateToBestOfWolt,
  };
};
